<template>
  <div class="bg-yellow-100 p-4 min-h-screen">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="mb-2 font-black text-black text-6xl text-center uppercase tracking-wider">
        🎤 Voice Grocery
      </h1>
      <p class="font-bold text-gray-800 text-2xl text-center uppercase tracking-wide">
        Inventory Manager
      </p>
    </div>

    <!-- Main Voice Interface -->
    <div class="mx-auto mb-8 max-w-4xl">
      <BrutalCard title="Voice Control" color="white" shadow="lg" class="relative mb-6">
        <!-- Loading overlay when processing -->
        <div v-if="isProcessing"
          class="z-10 absolute inset-0 flex justify-center items-center bg-white bg-opacity-90 border-4 border-black">
          <div class="text-center">
            <div class="mx-auto mb-4 border-b-4 border-blue-500 rounded-full w-16 h-16 animate-spin"></div>
            <p class="font-bold text-gray-800 text-xl uppercase tracking-wide">Processing Voice...</p>
            <p class="mt-2 text-gray-600">Please wait while we analyze your command</p>
          </div>
        </div>

        <div class="text-center">
          <VoiceRecordButton :is-recording="isRecording" :is-processing="isProcessing" :disabled="isProcessing"
            :status-message="statusMessage" @click="toggleRecording" />
          <p class="mt-2 text-gray-500 text-sm">
            or hold <kbd
              class="bg-gray-100 px-1.5 py-1 border border-gray-200 rounded-md font-semibold text-gray-800 text-xs">Alt</kbd>
            + <kbd
              class="bg-gray-100 px-1.5 py-1 border border-gray-200 rounded-md font-semibold text-gray-800 text-xs">O</kbd>
            to record
          </p>

          <!-- Live Transcription -->
          <div v-if="liveTranscription" class="mt-6">
            <BrutalCard color="blue" shadow="sm">
              <div class="text-center">
                <h4 class="mb-2 font-bold text-gray-600 text-sm uppercase tracking-wide">
                  Live Transcription
                </h4>
                <p class="font-medium text-gray-800 text-lg">
                  "{{ liveTranscription }}"
                </p>
              </div>
            </BrutalCard>
          </div>
        </div>
      </BrutalCard>

      <!-- Voice Response -->
      <div v-if="voiceResponse" class="mb-6">
        <BrutalCard color="green" shadow="md">
          <div class="flex justify-between items-start">
            <div class="flex-1">
              <h4 class="mb-2 font-bold text-gray-600 text-sm uppercase tracking-wide">
                🤖 Assistant Response
              </h4>
              <p class="mb-4 font-medium text-gray-800 text-lg">
                {{ voiceResponse }}
              </p>
            </div>
            <BrutalButton variant="secondary" size="sm" @click="readResponseAloud(voiceResponse)"
              :disabled="isSpeaking">
              {{ isSpeaking ? 'Speaking...' : '🔊 Read Aloud' }}
            </BrutalButton>
          </div>
        </BrutalCard>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="mx-auto mb-8 max-w-6xl">
      <h2 class="mb-6 font-black text-3xl text-center uppercase tracking-wider">
        ⚡ Quick Actions ⚡
      </h2>

      <div class="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <BrutalCard color="blue" shadow="md" :class="[
          'hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all hover:translate-x-1 hover:translate-y-1 duration-150',
          isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        ]">
          <a href="/inventory/export" download class="block py-4 text-center">
            <div class="mb-2 text-4xl">📥</div>
            <h3 class="font-bold text-lg uppercase tracking-wide">
              Export Inventory
            </h3>
            <p class="mt-1 text-gray-600 text-sm">
              Download as CSV file
            </p>
          </a>
        </BrutalCard>

        <BrutalCard color="yellow" shadow="md" :class="[
          'hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all hover:translate-x-1 hover:translate-y-1 duration-150',
          isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        ]" @click="!isProcessing && speakCommand('what items are low on stock')">
          <div class="py-4 text-center">
            <div class="mb-2 text-4xl">⚠️</div>
            <h3 class="font-bold text-lg uppercase tracking-wide">
              Low Stock
            </h3>
            <p class="mt-1 text-gray-600 text-sm">
              {{ stats.low_stock_count }} items running low
            </p>
          </div>
        </BrutalCard>

        <BrutalCard color="purple" shadow="md" :class="[
          'hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all hover:translate-x-1 hover:translate-y-1 duration-150',
          isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        ]" @click="!isProcessing && showFullInventory()">
          <div class="py-4 text-center">
            <div class="mb-2 text-4xl">📋</div>
            <h3 class="font-bold text-lg uppercase tracking-wide">
              Full Inventory
            </h3>
            <p class="mt-1 text-gray-600 text-sm">
              {{ stats.total_items }} total items
            </p>
          </div>
        </BrutalCard>

        <!-- Settings Gear Icon Card -->
        <BrutalCard color="gray" shadow="md" :class="[
          'hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all hover:translate-x-1 hover:translate-y-1 duration-150',
          'cursor-pointer'
        ]" @click="() => $inertia.visit('/settings')">
          <div class="flex flex-col items-center py-4 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="mb-2 w-10 h-10 text-gray-500" fill="none" viewBox="0 0 24 24"
              stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M11.049 2.927c.3-1.14 1.952-1.14 2.252 0a1.724 1.724 0 002.573 1.01c.943-.545 2.042.454 1.497 1.397a1.724 1.724 0 001.01 2.573c1.14.3 1.14 1.952 0 2.252a1.724 1.724 0 00-1.01 2.573c.545.943-.454 2.042-1.397 1.497a1.724 1.724 0 00-2.573 1.01c-.3 1.14-1.952 1.14-2.252 0a1.724 1.724 0 00-2.573-1.01c-.943.545-2.042-.454-1.497-1.397a1.724 1.724 0 00-1.01-2.573c-1.14-.3-1.14-1.952 0-2.252a1.724 1.724 0 001.01-2.573c-.545-.943.454-2.042 1.397-1.497.943.545 2.042-.454 1.497-1.397z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <h3 class="font-bold text-lg uppercase tracking-wide">
              Settings
            </h3>
            <p class="mt-1 text-gray-600 text-sm">
              Configure TTS and more
            </p>
          </div>
        </BrutalCard>
        <!-- End Settings Gear Icon Card -->
      </div>
    </div>

    <!-- Stats Dashboard -->
    <div class="mx-auto mb-8 max-w-6xl">
      <div class="gap-6 grid grid-cols-1 md:grid-cols-3">
        <!-- Current Inventory -->
        <BrutalCard title="Current Inventory" color="white" shadow="lg">
          <div class="space-y-3">
            <div v-for="item in grocery_items.slice(0, 5)" :key="item.id"
              class="group hover:bg-gray-50 p-2 rounded transition-colors">
              <div class="flex justify-between items-center">
                <div class="flex flex-1 items-center">
                  <div :class="[
                    'w-3 h-3 rounded-full mr-3',
                    item.stock_status === 'out_of_stock' ? 'bg-red-500' :
                      item.stock_status === 'low_stock' ? 'bg-yellow-500' : 'bg-green-500'
                  ]"></div>
                  <span class="font-medium">{{ item.name }}</span>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="font-bold text-gray-600 text-sm">
                    {{ item.formatted_quantity }}
                  </span>
                  <!-- Quick quantity adjustment buttons -->
                  <div class="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button @click="quickRemove(item, 1)" :disabled="isProcessing || item.current_quantity <= 0"
                      class="bg-red-400 hover:bg-red-500 disabled:opacity-50 border-2 border-black rounded w-6 h-6 font-bold text-white text-xs disabled:cursor-not-allowed"
                      title="Remove 1">
                      -1
                    </button>
                    <button @click="deleteItemCompletely(item)" :disabled="isProcessing"
                      class="bg-red-600 hover:bg-red-700 disabled:opacity-50 border-2 border-black rounded w-6 h-6 font-bold text-white text-xs disabled:cursor-not-allowed"
                      title="Delete completely">
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="grocery_items.length > 5" class="pt-2 text-center">
              <span class="text-gray-500 text-sm">
                ...and {{ grocery_items.length - 5 }} more items
              </span>
              <BrutalButton variant="secondary" size="sm" @click="showFullInventory" class="mt-2">
                Manage All Items
              </BrutalButton>
            </div>
          </div>
        </BrutalCard>

        <!-- Low Stock Items -->
        <BrutalCard title="Low Stock Alert" color="red" shadow="lg">
          <div class="space-y-3">
            <div v-for="item in low_stock_items.slice(0, 5)" :key="item.id" class="flex justify-between items-center">
              <span class="font-medium">{{ item.name }}</span>
              <span class="font-bold text-red-600 text-sm">
                {{ item.formatted_quantity }}
              </span>
            </div>
            <div v-if="low_stock_items.length === 0" class="py-4 text-center">
              <span class="text-gray-500">All items well stocked! 🎉</span>
            </div>
          </div>
        </BrutalCard>

        <!-- Recent Commands -->
        <BrutalCard title="Recent Commands" color="gray" shadow="lg">
          <div class="space-y-3">
            <div v-for="command in recent_commands" :key="command.id" class="pb-2 border-gray-200 border-b-2">
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <p class="font-medium text-gray-800 text-sm">
                    "{{ command.transcribed_text }}"
                  </p>
                  <p class="mt-1 text-gray-500 text-xs">
                    {{ command.formatted_time }}
                  </p>
                </div>
                <div class="flex items-center">
                  <span :class="[
                    'px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wide',
                    command.status === 'processed' ? 'bg-green-200 text-green-800' :
                      command.status === 'failed' ? 'bg-red-200 text-red-800' :
                        'bg-yellow-200 text-yellow-800'
                  ]">
                    {{ command.status }}
                  </span>
                  <button @click="reprocessCommand(command)" class="ml-2 text-blue-600 hover:text-blue-800 text-xs">
                    🔄
                  </button>
                </div>
              </div>
            </div>
          </div>
        </BrutalCard>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, computed, onMounted, ref } from 'vue'
import { useVoiceRecognition } from '@/composables/useVoiceRecognition'
import { useVoiceSynthesis } from '@/composables/useVoiceSynthesis'
import { useInventoryCommands } from '@/composables/useInventoryCommands'
import BrutalCard from '@/components/ui/BrutalCard.vue'
import BrutalButton from '@/components/ui/BrutalButton.vue'
import VoiceRecordButton from '@/components/ui/VoiceRecordButton.vue'
import { router } from '@inertiajs/vue3'

// Props from the Rails controller
interface Props {
  grocery_items: any[]
  categories: any[]
  recent_commands: any[]
  low_stock_items: any[]
  stats: {
    total_items: number
    low_stock_count: number
    out_of_stock_count: number
  }
}

defineProps<Props>()

// State for keyboard shortcut
const isKeyDown = ref(false)

// Voice recognition composable
const {
  isRecording,
  isProcessing: isVoiceProcessing,
  startRecording,
  stopRecording,
  liveTranscription,
  statusMessage,
  setProcessing,
  cleanup: cleanupVoiceRecognition
} = useVoiceRecognition()

// Voice synthesis composable
const {
  isSpeaking,
  readResponseAloud,
  initVoiceSynthesis // <-- import the new init function
} = useVoiceSynthesis()

// Inventory commands composable
const {
  isProcessing: isCommandProcessing,
  voiceResponse,
  processTextCommand,
  reprocessCommand,
  clearResponse
} = useInventoryCommands()

// Computed processing state that combines both processing states
const isProcessing = computed(() => isVoiceProcessing.value || isCommandProcessing.value)

const toggleRecording = async () => {
  if (isRecording.value) {
    const text = await stopRecording()
    await processVoiceInput(text)
  } else {
    clearResponse()
    startRecording()
  }
}

const processVoiceInput = async (transcript: string) => {
  try {
    if (!transcript.trim()) {
      voiceResponse.value = 'No speech detected. Please try again.'
      return
    }
    const result = await processTextCommand(transcript)
    voiceResponse.value = result.response

    // Auto-read response if needed
    if (result.shouldReadAloud) {
      setTimeout(() => {
        readResponseAloud(result.response)
      }, 500)
    }
  } catch (error) {
    console.error('Voice processing error:', error)
    voiceResponse.value = 'Sorry, I couldn\'t process that command. Please try again.'
  } finally {
    // Clear the voice processing state when complete
    setProcessing(false)
  }
}

const speakCommand = async (command: string) => {
  try {
    clearResponse()
    const result = await processTextCommand(command)
    voiceResponse.value = result.response

    // Auto-read response for quick actions
    if (result.shouldReadAloud) {
      setTimeout(() => {
        readResponseAloud(result.response)
      }, 500)
    }
  } catch (error) {
    console.error('Command processing error:', error)
    voiceResponse.value = 'Sorry, I couldn\'t process that command. Please try again.'
  }
}

// Quick delete functions
const quickRemove = async (item: any, quantity: number) => {
  try {
    const command = `remove ${quantity} ${item.name}`
    const result = await processTextCommand(command)
    voiceResponse.value = result.response

    if (result.shouldReadAloud) {
      setTimeout(() => {
        readResponseAloud(result.response)
      }, 500)
    }
  } catch (error) {
    console.error('Quick remove error:', error)
    voiceResponse.value = `Failed to remove ${item.name}. Please try again.`
  }
}

const deleteItemCompletely = async (item: any) => {
  if (confirm(`Are you sure you want to completely delete "${item.name}" from your inventory?`)) {
    try {
      const response = await fetch(`/api/grocery_items/${item.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })

      const result = await response.json()

      if (result.success) {
        voiceResponse.value = `Successfully deleted ${item.name} from your inventory.`
        // Refresh the page data to reflect changes
        location.reload() // Simple refresh for now
      } else {
        voiceResponse.value = `Failed to delete ${item.name}. Please try again.`
      }
    } catch (error) {
      console.error('Delete error:', error)
      voiceResponse.value = `Failed to delete ${item.name}. Please try again.`
    }
  }
}

const showFullInventory = () => {
  // Navigate to the detailed inventory management page
  router.visit('/inventory/manage')
}

// Keyboard shortcut handlers
const handleKeyDown = (event: KeyboardEvent) => {
  // Shortcut: Alt + O
  if (event.altKey && event.key === 'o') {
    // Prevent default browser action (e.g., opening a menu)
    event.preventDefault()

    // Don't do anything if we are typing in an input
    const target = event.target as HTMLElement
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
      return
    }

    // Start recording only if not already recording and key is not already down
    if (!isRecording.value && !isKeyDown.value) {
      isKeyDown.value = true
      clearResponse()
      startRecording()
    }
  }
}

const handleKeyUp = async (event: KeyboardEvent) => {
  // If the shortcut was active, and either key is released, stop recording.
  if (isKeyDown.value && (event.key === 'o' || event.key === 'Alt')) {
    event.preventDefault()

    // Prevent stopping twice if both keys are released quickly
    if (isRecording.value) {
      const text = await stopRecording()
      await processVoiceInput(text)
    }
    isKeyDown.value = false
  }
}


// Cleanup on unmount
onUnmounted(() => {
  cleanupVoiceRecognition()
  window.removeEventListener('keydown', handleKeyDown)
  window.removeEventListener('keyup', handleKeyUp)
})

onMounted(() => {
  initVoiceSynthesis()
  window.addEventListener('keydown', handleKeyDown)
  window.addEventListener('keyup', handleKeyUp)
})
</script>